import React, { useEffect, useCallback, useRef } from 'react';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import './introjs-theme.css';
import { api } from '@/contexts/AuthContext';

interface GuidedTourProps {
    userId: string;
    hasSeenOnboarding?: boolean;
    forceStart?: boolean;
    onTourEnd?: () => void;
}

// ── Helper: modal de video ────────────────────────────────────────────────────

function openVideoModal(onClose: () => void): void {
    const existing = document.getElementById('tour-video-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'tour-video-modal';
    overlay.style.cssText = [
        'position:fixed;inset:0;z-index:99999',
        'background:rgba(0,0,0,0.92)',
        'display:flex;flex-direction:column;align-items:center;justify-content:center',
        'animation:tourFadeIn 0.25s ease',
    ].join(';');

    overlay.innerHTML = `
        <style>
            @keyframes tourFadeIn { from { opacity:0; transform:scale(0.97) } to { opacity:1; transform:scale(1) } }
        </style>
        <div style="position:relative;width:min(90vw,960px);display:flex;flex-direction:column;gap:14px">
            <div style="display:flex;align-items:center;justify-content:space-between">
                <span style="color:#fff;font-size:15px;font-weight:600;font-family:inherit">
                    🎬 Tutorial de Conexión WhatsApp
                </span>
                <button id="tour-video-close"
                    style="background:rgba(255,255,255,0.1);border:1px solid rgba(255,255,255,0.2);
                           color:#fff;border-radius:8px;padding:6px 14px;font-size:13px;
                           font-weight:600;cursor:pointer;font-family:inherit">
                    Cerrar y continuar →
                </button>
            </div>
            <div style="border-radius:12px;overflow:hidden;line-height:0;box-shadow:0 24px 64px rgba(0,0,0,0.6)">
                <video id="tour-video-player"
                    style="width:100%;display:block;background:#000;max-height:70vh"
                    controls autoplay preload="metadata">
                    <source src="https://storage.googleapis.com/reveca-whatsapp-media/tutorials/conexion%20Whatsapp%20verificaci%C3%B3n.mp4" type="video/mp4" />
                    Tu navegador no soporta la reproducción de videos.
                </video>
            </div>
            <p style="text-align:center;color:#94a3b8;font-size:12px;margin:0;font-family:inherit">
                Puedes cerrar el video en cualquier momento y continuar con la configuración.
            </p>
        </div>
    `;

    document.body.appendChild(overlay);

    const close = () => {
        const player = document.getElementById('tour-video-player') as HTMLVideoElement | null;
        if (player) { try { player.pause(); } catch (_) { } }
        overlay.remove();
        onClose();
    };

    document.getElementById('tour-video-close')?.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
}

// ── Shared: crea los 4 paneles oscuros + anillo en un root fijo ───────────────

function buildSpotlightRoot(id: string) {
    const root = document.createElement('div');
    root.id = id;
    root.style.cssText = 'position:fixed;inset:0;z-index:99998;pointer-events:none;';

    const mkPanel = () => {
        const d = document.createElement('div');
        d.style.cssText = 'position:absolute;background:rgba(15,23,42,0.82);pointer-events:auto;transition:none;';
        return d;
    };

    const pTop = mkPanel();
    const pBot = mkPanel();
    const pLft = mkPanel();
    const pRgt = mkPanel();

    const ring = document.createElement('div');
    ring.style.cssText = [
        'position:absolute',
        'border:2px solid rgba(28,179,179,0.9)',
        'border-radius:10px',
        'animation:tutSpotPulse 2s infinite ease-in-out',
        'pointer-events:none',
        'transition:none',
    ].join(';');

    root.appendChild(pTop);
    root.appendChild(pBot);
    root.appendChild(pLft);
    root.appendChild(pRgt);
    root.appendChild(ring);

    /** Actualiza posición de los 4 paneles y el anillo en base al rect actual */
    const update = (r: DOMRect, pad: number) => {
        pTop.style.top = '0'; pTop.style.left = '0'; pTop.style.right = '0';
        pTop.style.height = `${r.top - pad}px`;

        pBot.style.top = `${r.bottom + pad}px`; pBot.style.left = '0';
        pBot.style.right = '0'; pBot.style.bottom = '0'; pBot.style.height = '';

        pLft.style.top = `${r.top - pad}px`; pLft.style.left = '0';
        pLft.style.width = `${r.left - pad}px`;
        pLft.style.height = `${r.height + pad * 2}px`;

        pRgt.style.top = `${r.top - pad}px`;
        pRgt.style.left = `${r.right + pad}px`;
        pRgt.style.right = '0';
        pRgt.style.height = `${r.height + pad * 2}px`;

        ring.style.top = `${r.top - pad}px`;
        ring.style.left = `${r.left - pad}px`;
        ring.style.width = `${r.width + pad * 2}px`;
        ring.style.height = `${r.height + pad * 2}px`;
    };

    return { root, update };
}

// ── Helper: spotlight sobre #whatsapp-tutorial-header ────────────────────────

function showTutorialSpotlight(onContinue: () => void): void {
    document.getElementById('tour-tutorial-spotlight')?.remove();

    const el = document.getElementById('whatsapp-tutorial-header');
    if (!el) { onContinue(); return; }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
        const pad = 10;
        const CARD_W = 310;

        const { root, update } = buildSpotlightRoot('tour-tutorial-spotlight');

        // Card (debajo del botón)
        const card = document.createElement('div');
        card.style.cssText = [
            `position:absolute`,
            `width:${CARD_W}px`,
            `background:linear-gradient(135deg,#1a2e4a,#0f1f35)`,
            `border:1px solid rgba(28,179,179,0.4)`,
            `border-radius:16px`,
            `padding:20px 18px 18px`,
            `color:#e2e8f0`,
            `font-family:'Inter','Segoe UI',system-ui,sans-serif`,
            `pointer-events:auto`,
            `box-shadow:0 24px 64px rgba(0,0,0,0.7)`,
            `animation:tutCardIn 0.3s ease`,
        ].join(';');

        card.innerHTML = `
            <style>
                @keyframes tutSpotPulse {
                    0%   { box-shadow: 0 0 0 0   rgba(28,179,179,0.5); }
                    50%  { box-shadow: 0 0 0 10px rgba(28,179,179,0);   }
                    100% { box-shadow: 0 0 0 0   rgba(28,179,179,0);   }
                }
                @keyframes tutCardIn {
                    from { opacity:0; transform:translateY(10px); }
                    to   { opacity:1; transform:translateY(0);    }
                }
                #tut-continue-btn:hover {
                    background: linear-gradient(135deg,#22d3d3,#1CB3B3) !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 16px rgba(28,179,179,0.45) !important;
                }
            </style>
            <div id="tut-arrow" style="position:absolute;top:-9px;width:0;height:0;
                        border-left:9px solid transparent;
                        border-right:9px solid transparent;
                        border-bottom:9px solid rgba(28,179,179,0.4);">
            </div>
            <div style="text-align:center">
                <div style="font-size:30px;margin-bottom:10px">🎬</div>
                <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#f0f9ff;letter-spacing:-0.2px">
                    Aquí puedes ver el video
                </p>
                <p style="margin:0 0 16px;color:#94a3b8;font-size:13px;line-height:1.55">
                    Haz clic en <strong style="color:#5eead4">Tutorial de Conexión</strong>
                    para abrirlo cuantas veces desees.
                </p>
                <button id="tut-continue-btn"
                    style="width:100%;padding:10px 0;border-radius:8px;font-size:13px;
                           font-weight:600;cursor:pointer;
                           background:linear-gradient(135deg,#1CB3B3,#0d9191);
                           color:white;border:none;font-family:inherit;
                           transition:all 0.18s ease;">
                    Continuar →
                </button>
            </div>
        `;

        root.appendChild(card);
        document.body.appendChild(root);

        const arrow = document.getElementById('tut-arrow');

        /** Recalcula todo en base a la posición actual del elemento */
        const layout = () => {
            const r = el.getBoundingClientRect();
            update(r, pad);

            let left = r.left + r.width / 2 - CARD_W / 2;
            left = Math.max(12, Math.min(left, window.innerWidth - CARD_W - 12));
            card.style.left = `${left}px`;
            card.style.top = `${r.bottom + pad + 14}px`;

            if (arrow) {
                const arrowL = Math.min(Math.max(r.left + r.width / 2 - left - 9, 14), CARD_W - 28);
                arrow.style.left = `${arrowL}px`;
            }
        };

        layout(); // posición inicial

        // Scroll tracking con rAF throttle
        let rafId: number | null = null;
        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => { rafId = null; layout(); });
        };
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });

        document.getElementById('tut-continue-btn')?.addEventListener('click', () => {
            window.removeEventListener('scroll', onScroll, { capture: true });
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            root.remove();
            onContinue();
        });

    }, 500);
}

// ── Helper: spotlight sobre #whatsapp-connect-btn ────────────────────────────

function showConnectSpotlight(onContinue: () => void): void {
    document.getElementById('tour-connect-spotlight')?.remove();

    const el = document.getElementById('whatsapp-connect-btn');
    if (!el) { onContinue(); return; }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
        const pad = 10;
        const CARD_W = 310;

        const { root, update } = buildSpotlightRoot('tour-connect-spotlight');

        // Card: renderizar invisible primero para medir altura real
        const card = document.createElement('div');
        card.style.cssText = [
            `position:absolute`,
            `top:-9999px`,
            `width:${CARD_W}px`,
            `visibility:hidden`,
            `background:linear-gradient(135deg,#1a2e4a,#0f1f35)`,
            `border:1px solid rgba(28,179,179,0.4)`,
            `border-radius:16px`,
            `padding:20px 18px 18px`,
            `color:#e2e8f0`,
            `font-family:'Inter','Segoe UI',system-ui,sans-serif`,
            `pointer-events:none`,
            `box-shadow:0 24px 64px rgba(0,0,0,0.7)`,
        ].join(';');

        card.innerHTML = `
            <style>
                #conn-continue-btn:hover {
                    background: linear-gradient(135deg,#22d3d3,#1CB3B3) !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 16px rgba(28,179,179,0.45) !important;
                }
            </style>
            <div id="conn-arrow" style="position:absolute;top:auto;width:0;height:0;
                        border-left:9px solid transparent;
                        border-right:9px solid transparent;
                        border-top:9px solid rgba(28,179,179,0.4);
                        border-bottom:none;">
            </div>
            <div style="text-align:center">
                <div style="font-size:30px;margin-bottom:10px">📲</div>
                <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#f0f9ff;letter-spacing:-0.2px">
                    Conecta tu WhatsApp
                </p>
                <p style="margin:0 0 16px;color:#94a3b8;font-size:13px;line-height:1.55">
                    Haz clic en <strong style="color:#5eead4">Conectar WhatsApp</strong>
                    para iniciar el proceso de vinculación.
                </p>
                <button id="conn-continue-btn"
                    style="width:100%;padding:10px 0;border-radius:8px;font-size:13px;
                           font-weight:600;cursor:pointer;
                           background:linear-gradient(135deg,#1CB3B3,#0d9191);
                           color:white;border:none;font-family:inherit;
                           transition:all 0.18s ease;">
                    ¡Entendido! 🚀
                </button>
            </div>
        `;

        root.appendChild(card);
        document.body.appendChild(root);

        // Medir altura real una sola vez
        const realH = card.offsetHeight;
        card.style.visibility = '';
        card.style.pointerEvents = 'auto';
        card.style.animation = 'tutCardIn 0.3s ease';

        const arrow = document.getElementById('conn-arrow');

        /** Recalcula posición de paneles, anillo y card encima del botón */
        const layout = () => {
            const r = el.getBoundingClientRect();
            update(r, pad);

            let left = r.left + r.width / 2 - CARD_W / 2;
            left = Math.max(12, Math.min(left, window.innerWidth - CARD_W - 12));
            card.style.left = `${left}px`;
            card.style.top = `${r.top - pad - 20 - realH}px`;

            if (arrow) {
                const arrowL = Math.min(Math.max(r.left + r.width / 2 - left - 9, 14), CARD_W - 28);
                arrow.style.left = `${arrowL}px`;
                arrow.style.bottom = '-9px';
                arrow.style.top = 'auto';
            }
        };

        layout(); // posición inicial

        // Scroll tracking con rAF throttle
        let rafId: number | null = null;
        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => { rafId = null; layout(); });
        };
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });

        document.getElementById('conn-continue-btn')?.addEventListener('click', () => {
            window.removeEventListener('scroll', onScroll, { capture: true });
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            root.remove();
            onContinue();
        });

    }, 500);
}

function showNewCampaignSpotlight(onContinue: () => void, attempts = 0): void {
    document.getElementById('tour-campaigns-spotlight')?.remove();

    // Buscar el botón principal de crear campaña en el centro de la pantalla
    const navBtn = document.getElementById('tour-new-campaign-btn');
    if (!navBtn) {
        if (attempts < 10) {
            setTimeout(() => showNewCampaignSpotlight(onContinue, attempts + 1), 300);
            return;
        }
        onContinue(); return;
    }

    navBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
        const pad = 5;
        const CARD_W = 320;

        const { root, update } = buildSpotlightRoot('tour-campaigns-spotlight');

        const card = document.createElement('div');
        card.style.cssText = [
            `position:absolute`,
            `top:-9999px`,
            `width:${CARD_W}px`,
            `visibility:hidden`,
            `background:linear-gradient(135deg,#1a2e4a,#0f1f35)`,
            `border:1px solid rgba(28,179,179,0.4)`,
            `border-radius:16px`,
            `padding:20px 18px 18px`,
            `color:#e2e8f0`,
            `font-family:'Inter','Segoe UI',system-ui,sans-serif`,
            `pointer-events:none`,
            `box-shadow:0 24px 64px rgba(0,0,0,0.7)`,
        ].join(';');

        card.innerHTML = `
            <style>
                #camp-continue-btn:hover {
                    background: linear-gradient(135deg,#22d3d3,#1CB3B3) !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 16px rgba(28,179,179,0.45) !important;
                }
            </style>
            <div id="camp-arrow" style="position:absolute;top:-9px;width:0;height:0;
                        border-left:9px solid transparent;
                        border-right:9px solid transparent;
                        border-bottom:9px solid rgba(28,179,179,0.4);">
            </div>
            <div style="text-align:center">
                <div style="font-size:32px;margin-bottom:12px">🚀</div>
                <p style="margin:0 0 6px;font-size:16px;font-weight:700;color:#f0f9ff;letter-spacing:-0.2px">
                    ¡Es hora de crear tu primera campaña!
                </p>
                <p style="margin:0 0 16px;color:#94a3b8;font-size:13px;line-height:1.55">
                    Ya has conectado WhatsApp. Haz clic aquí para definir tu objetivo y empezar a enviar mensajes.
                </p>
                <button id="camp-continue-btn"
                    style="width:100%;padding:10px 0;border-radius:8px;font-size:13px;
                           font-weight:600;cursor:pointer;
                           background:linear-gradient(135deg,#1CB3B3,#0d9191);
                           color:white;border:none;font-family:inherit;
                           transition:all 0.18s ease;">
                    Crear Campaña →
                </button>
            </div>
        `;

        root.appendChild(card);
        document.body.appendChild(root);

        const realH = card.offsetHeight;
        card.style.visibility = '';
        card.style.pointerEvents = 'auto';
        card.style.animation = 'tutCardIn 0.3s ease';

        const arrow = document.getElementById('camp-arrow');

        const layout = () => {
            const r = navBtn.getBoundingClientRect();
            update(r, pad);

            // Centrar tarjeta horizontalmente con el botón
            let left = r.left + r.width / 2 - CARD_W / 2;
            left = Math.max(12, Math.min(left, window.innerWidth - CARD_W - 12));
            card.style.left = left + 'px';

            // Posicionar tarjeta DEBAJO del botón principal
            let top = r.bottom + pad + 15;
            card.style.top = top + 'px';

            if (arrow) {
                // Flecha apuntando hacia arriba hacia el botón
                const arrowL = Math.min(Math.max(r.left + r.width / 2 - left - 9, 14), CARD_W - 28);
                arrow.style.left = arrowL + 'px';
            }
        };

        layout();

        let rafId: number | null = null;
        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => { rafId = null; layout(); });
        };
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });

        document.getElementById('camp-continue-btn')?.addEventListener('click', () => {
            window.removeEventListener('scroll', onScroll, { capture: true });
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            root.remove();

            navBtn.click();
            onContinue();
        });

    }, 500);
}

function showCampaignsNavSpotlight(onContinue: () => void): void {
    document.getElementById('tour-campaigns-nav')?.remove();

    const navBtn = document.getElementById('nav-renewals'); // sidebar Campaigns button
    if (!navBtn) { onContinue(); return; }

    navBtn.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
        const pad = 5;
        const CARD_W = 310;

        const { root, update } = buildSpotlightRoot('tour-campaigns-nav');

        const card = document.createElement('div');
        card.style.cssText = [
            `position:absolute`,
            `top:-9999px`,
            `width:${CARD_W}px`,
            `visibility:hidden`,
            `background:linear-gradient(135deg,#1a2e4a,#0f1f35)`,
            `border:1px solid rgba(28,179,179,0.4)`,
            `border-radius:16px`,
            `padding:20px 18px 18px`,
            `color:#e2e8f0`,
            `font-family:'Inter','Segoe UI',system-ui,sans-serif`,
            `pointer-events:none`,
            `box-shadow:0 24px 64px rgba(0,0,0,0.7)`,
        ].join(';');

        card.innerHTML = `
            <style>
                #nav-continue-btn:hover {
                    background: linear-gradient(135deg,#22d3d3,#1CB3B3) !important;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 16px rgba(28,179,179,0.45) !important;
                }
            </style>
            <div id="nav-arrow" style="position:absolute;left:-9px;width:0;height:0;
                        border-top:9px solid transparent;
                        border-bottom:9px solid transparent;
                        border-right:9px solid rgba(28,179,179,0.4);">
            </div>
            <div style="text-align:center">
                <div style="font-size:30px;margin-bottom:10px">🚀</div>
                <p style="margin:0 0 6px;font-size:15px;font-weight:700;color:#f0f9ff;letter-spacing:-0.2px">
                    ¡Es hora de crear tu campaña!
                </p>
                <p style="margin:0 0 16px;color:#94a3b8;font-size:13px;line-height:1.55">
                    Ya conectaste WhatsApp. Haz clic en <strong style="color:#5eead4">Campañas</strong>
                    para empezar a crear tus recordatorios.
                </p>
                <button id="nav-continue-btn"
                    style="width:100%;padding:10px 0;border-radius:8px;font-size:13px;
                           font-weight:600;cursor:pointer;
                           background:linear-gradient(135deg,#1CB3B3,#0d9191);
                           color:white;border:none;font-family:inherit;
                           transition:all 0.18s ease;">
                    Ir a Campañas →
                </button>
            </div>
        `;

        root.appendChild(card);
        document.body.appendChild(root);

        const realH = card.offsetHeight;
        card.style.visibility = '';
        card.style.pointerEvents = 'auto';
        card.style.animation = 'tutCardIn 0.3s ease';

        const arrow = document.getElementById('nav-arrow');

        const layout = () => {
            const r = navBtn.getBoundingClientRect();
            update(r, pad);

            // Posicionar tarjeta a la derecha del botón del menú lateral
            let left = r.right + pad + 15;
            left = Math.min(left, window.innerWidth - CARD_W - 12);
            card.style.left = left + 'px';

            // Centrar verticalmente
            let top = r.top + r.height / 2 - realH / 2;
            top = Math.max(12, Math.min(top, window.innerHeight - realH - 12));
            card.style.top = top + 'px';

            if (arrow) {
                // Flecha a la izquierda
                const arrowT = Math.min(Math.max(r.top + r.height / 2 - top - 9, 14), realH - 28);
                arrow.style.top = arrowT + 'px';
            }
        };

        layout();

        let rafId: number | null = null;
        const onScroll = () => {
            if (rafId !== null) return;
            rafId = requestAnimationFrame(() => { rafId = null; layout(); });
        };
        window.addEventListener('scroll', onScroll, { passive: true, capture: true });

        document.getElementById('nav-continue-btn')?.addEventListener('click', () => {
            window.removeEventListener('scroll', onScroll, { capture: true });
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            root.remove();

            // Automáticamente hacer clic en Campañas
            navBtn.click();

            onContinue();
        });

    }, 500);
}

// ─────────────────────────────────────────────────────────────────────────────

const GuidedTour: React.FC<GuidedTourProps> = ({ userId, hasSeenOnboarding, forceStart, onTourEnd }) => {
    const introRef = useRef<ReturnType<typeof introJs> | null>(null);
    const reinitializingRef = useRef(false);

    const markTourSeen = useCallback(async () => {
        try { await api.put('/auth/me/onboarding-tour'); }
        catch (e) { console.error('Failed to persist onboarding-tour flag', e); }
    }, []);

    const cleanExit = () => {
        if (!introRef.current) return;
        reinitializingRef.current = true;
        try { introRef.current.exit(true); } catch (_) { }
        introRef.current = null;
        reinitializingRef.current = false;
        document.getElementById('tour-video-modal')?.remove();
        document.getElementById('tour-tutorial-spotlight')?.remove();
        document.getElementById('tour-connect-spotlight')?.remove();
        document.getElementById('tour-campaigns-spotlight')?.remove();
    };

    const startTour = useCallback(async (isForced: boolean) => {
        cleanExit();

        const clearWizardKeys = () => {
            const keys = [
                'marketing_tour_stage1_seen', 'marketing_tour_stage2_seen', 'marketing_tour_stage3_seen', 'marketing_tour_stage4_seen',
                'marketing_tour_step3_seen', 'marketing_tour_step4_seen', 'marketing_tour_step5_seen',
                'marketing_tour_create_template_seen', 'marketing_tour_create_template_msg_seen',
                'renewal_tour_stage1_seen', 'renewal_tour_stage2_seen', 'renewal_tour_stage_columns_seen', 'renewal_tour_stage_scheduling_seen', 'renewal_tour_stage_reminders_seen',
                'renewal_tour_step3_seen', 'renewal_tour_step4_seen', 'renewal_tour_step5_seen',
                'renewal_tour_create_template_seen', 'renewal_tour_create_template_msg_seen',
                'dynamic_tour_stage1_seen', 'dynamic_tour_stage2_seen', 'dynamic_tour_stage_columns_seen', 'dynamic_tour_stage_reminders_seen',
                'dynamic_tour_step3_seen', 'dynamic_tour_step4_seen', 'dynamic_tour_step5_seen',
                'dynamic_tour_create_template_seen', 'dynamic_tour_create_template_msg_seen'
            ];
            keys.forEach(k => {
                localStorage.removeItem(k);
                sessionStorage.removeItem(k);
            });
        };

        if (isForced) {
            const path = window.location.pathname;
            if (path.startsWith('/campaign-builder') || path.startsWith('/renewal-builder')) {
                // ✅ DOM-based detection: check which wizard step is currently visible

                // Step 5: Campaign Preview
                if (document.getElementById('tour-marketing-preview-campaign')) {
                    clearWizardKeys();
                    setTimeout(() => {
                        import('./MarketingCampaignTour').then(({ startMarketingStep5Tour }) => {
                            startMarketingStep5Tour(() => { });
                        });
                    }, 300);
                    onTourEnd?.();
                    return true;
                }

                // Step 4: Variable Mapping
                if (document.getElementById('tour-marketing-open-sheet')) {
                    clearWizardKeys();
                    setTimeout(() => {
                        import('./MarketingCampaignTour').then(({ startMarketingStep4Tour }) => {
                            startMarketingStep4Tour(() => { });
                        });
                    }, 300);
                    onTourEnd?.();
                    return true;
                }

                // Step 3 (part 2): Create Template Message
                if (document.getElementById('messageEditor')) {
                    clearWizardKeys();
                    setTimeout(() => {
                        import('./MarketingCampaignTour').then(({ startMarketingCreateTemplateMessageTour }) => {
                            startMarketingCreateTemplateMessageTour(() => { });
                        });
                    }, 300);
                    onTourEnd?.();
                    return true;
                }

                // Step 3: Create Template inline form (approval warning visible)
                if (document.getElementById('tour-marketing-template-approval-warning')) {
                    clearWizardKeys();
                    setTimeout(() => {
                        import('./MarketingCampaignTour').then(({ startMarketingCreateTemplateTour }) => {
                            startMarketingCreateTemplateTour(() => { });
                        });
                    }, 300);
                    onTourEnd?.();
                    return true;
                }

                // Step 3: Template Selection (select approved template)
                if (document.getElementById('tour-marketing-template-select')) {
                    clearWizardKeys();
                    setTimeout(() => {
                        import('./MarketingCampaignTour').then(({ startMarketingStep3Tour }) => {
                            startMarketingStep3Tour(() => { });
                        });
                    }, 300);
                    onTourEnd?.();
                    return true;
                }

                // Step 2: Data / Marketing config - fallback to event dispatch
                const event = new CustomEvent('restart-local-tour', { detail: { handled: false } });
                window.dispatchEvent(event);
                if (event.detail.handled) {
                    onTourEnd?.();
                    return true;
                }
            }

            localStorage.removeItem('wizard_tour_seen');
            // ✅ Al re-activar manualmente, limpiar dismissal temporal para que funcione el tour
            localStorage.removeItem('tour_permanently_dismissed');
            clearWizardKeys();
        }

        let isWPConnected = false;
        try {
            // Verificar conexión contra la API antes de hacer cualquier tour asunciones visuales
            const res = await api.get('/whatsapp/connected-accounts');
            if (res.data?.success && res.data?.accounts && res.data.accounts.length > 0) {
                isWPConnected = true;
            }
        } catch (error) {
            console.error('GuidedTour: No se pudo verificar estado de WhatsApp:', error);
        }

        if (isWPConnected) {
            // 🔒 Bloqueo por variable de entorno: Post-conexión
            const isPostTourEnabled = process.env.REACT_APP_ENABLE_POST_TOUR !== 'false' && process.env.REACT_APP_ENABLE_POST_TOUR !== '0';
            if (!isPostTourEnabled) {
                // Marcar como visto para que no intente auto-iniciar de nuevo
                localStorage.setItem('tour_permanently_dismissed', 'true');
                if (!isForced) markTourSeen();
                onTourEnd?.();
                return false;
            }
            // ESCENARIO 2: Post-conexión (Muestra flow de Campañas globalmente)
            const done = () => {
                document.getElementById('tour-campaigns-nav')?.remove();
                document.getElementById('tour-campaigns-spotlight')?.remove();
                // ✅ Marcar permanentemente para no auto-iniciar nunca más
                localStorage.setItem('tour_permanently_dismissed', 'true');
                if (!isForced) markTourSeen();
                onTourEnd?.();
            };

            showCampaignsNavSpotlight(() => {
                // Al hacer clic, espera la pantalla y lanza spotlight 2
                setTimeout(() => {
                    showNewCampaignSpotlight(() => done());
                }, 500);
            });
            return true;
        }

        // ==== Si llegamos aquí, el usuario NO TIENE WhatsApp conectado ====

        // 🔒 Bloqueo por variable de entorno: Pre-conexión
        const isPreTourEnabled = process.env.REACT_APP_ENABLE_PRE_TOUR !== 'false' && process.env.REACT_APP_ENABLE_PRE_TOUR !== '0';
        if (!isPreTourEnabled) {
            // Marcar como visto para que no intente auto-iniciar de nuevo
            localStorage.setItem('tour_permanently_dismissed', 'true');
            if (!isForced) markTourSeen();
            onTourEnd?.();
            return false;
        }

        // SOLO REACCIONA AUTOMÁTICAMENTE SI ESTÁ EN /whatsapp (SPOTLIGHTS LOCALES PRE-CONEXIÓN)
        if (window.location.pathname === '/whatsapp') {
            // Pequeño timeout para evitar posibles glitches de renderizado del componente
            setTimeout(() => {
                const hasTutorial = document.getElementById('whatsapp-tutorial-header');
                if (hasTutorial) {
                    // ESCENARIO 1 LOCAL: Todavía en /whatsapp, sin conectar. (Spotlight sobre el video base)
                    const done = () => {
                        document.getElementById('tour-tutorial-spotlight')?.remove();
                        document.getElementById('tour-connect-spotlight')?.remove();
                        // ✅ Marcar permanentemente para no auto-iniciar nunca más
                        localStorage.setItem('tour_permanently_dismissed', 'true');
                        if (!isForced) markTourSeen();
                        onTourEnd?.();
                    };
                    showTutorialSpotlight(() => showConnectSpotlight(() => done()));
                }
            }, 600);
            return true;
        }

        // Si no estamos en /whatsapp, fallback normal de introJs global
        // ─────────────────────────────────────────────────────────────────────

        const intro = introJs();
        introRef.current = intro;

        // ── Button styles ────────────────────────────────────────────────────
        const cancelStyle = 'width:110px;padding:8px 0;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:#64748b;border:1px solid rgba(100,116,139,0.3);font-family:inherit';
        const nextStyle = 'width:110px;padding:8px 0;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;background:linear-gradient(135deg,#1CB3B3,#0d9191);color:white;border:none;font-family:inherit';
        const yesStyle = 'width:120px;padding:8px 0;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;background:linear-gradient(135deg,#1CB3B3,#0d9191);color:white;border:none;font-family:inherit';
        const noStyle = 'width:120px;padding:8px 0;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:#64748b;border:1px solid rgba(100,116,139,0.3);font-family:inherit';

        const step0HTML = [
            '<div style="text-align:center">',
            '  <h2 style="margin:0 0 8px">¡Bienvenido a Reveca! 🎉</h2>',
            '  <p style="margin:0 0 16px;color:#94a3b8">Soy tu guía y te acompañaré<br/>en tus primeros pasos por la plataforma.</p>',
            '  <div style="display:flex;gap:10px;justify-content:center;padding-top:14px;border-top:1px solid rgba(28,179,179,0.2)">',
            '    <button data-tour-action="cancel" style="' + cancelStyle + '">Cancelar</button>',
            '    <button data-tour-action="next"   style="' + nextStyle + '">Siguiente →</button>',
            '  </div>',
            '</div>',
        ].join('');

        const step1HTML = [
            '<div style="text-align:left">',
            '  <h3 style="margin:0 0 12px;font-size:16px;font-weight:700;text-align:center">📋 Antes de continuar</h3>',
            '  <p style="margin:0 0 10px;color:#94a3b8;font-size:13px;text-align:center">Para conectar WhatsApp necesitarás:</p>',
            '  <ul style="margin:0 0 14px;padding-left:18px;color:#cbd5e1;font-size:13px;line-height:1.7">',
            '    <li>Una cuenta de <strong style="color:#e2e8f0">WhatsApp Business</strong></li>',
            '    <li>Una <strong style="color:#e2e8f0">SIM card nueva</strong> (número dedicado para el negocio)</li>',
            '  </ul>',
            '  <div style="border-top:1px solid rgba(28,179,179,0.2);padding-top:14px;text-align:center">',
            '    <p style="margin:0 0 10px;font-size:13px;font-weight:600;color:#e2e8f0">🎬 ¿Quieres ver el video guía antes de conectar?</p>',
            '    <p style="margin:0 0 12px;color:#94a3b8;font-size:12px">Te explicamos todo en 4 minutos paso a paso.</p>',
            '    <div style="display:flex;gap:10px;justify-content:center">',
            '      <button data-tour-action="video-no"  style="' + noStyle + '">No, gracias</button>',
            '      <button data-tour-action="video-yes" style="' + yesStyle + '">Sí, ver video</button>',
            '    </div>',
            '  </div>',
            '</div>',
        ].join('');

        intro.setOptions({
            nextLabel: 'Siguiente →',
            prevLabel: '← Anterior',
            doneLabel: '¡Entendido!',
            exitOnOverlayClick: false,
            showStepNumbers: false,
            showBullets: false,
            overlayOpacity: 0.75,
            disableInteraction: false,
            scrollToElement: false,
            steps: [
                { intro: step0HTML, tooltipClass: 'step-0-card' },
                { intro: step1HTML, tooltipClass: 'step-0-card' },
            ],
        });

        // ── Botones personalizados (capture phase) ────────────────────────────
        const handleTourClick = (e: MouseEvent) => {
            const btn = (e.target as HTMLElement).closest('[data-tour-action]') as HTMLElement | null;
            if (!btn) return;
            e.stopImmediatePropagation();
            e.preventDefault();
            const action = btn.getAttribute('data-tour-action');

            if (action === 'cancel') {
                intro.exit(true);

            } else if (action === 'finish') {
                intro.exit(true);

            } else if (action === 'next') {
                if (window.location.pathname !== '/whatsapp') {
                    window.dispatchEvent(new CustomEvent('tour-navigate-whatsapp'));
                }
                intro.nextStep();

            } else if (action === 'video-yes') {
                const tooltip = document.querySelector('.introjs-tooltip') as HTMLElement | null;
                const overlay2 = document.querySelector('.introjs-overlay') as HTMLElement | null;
                const helper = document.querySelector('.introjs-helperLayer') as HTMLElement | null;
                if (tooltip) tooltip.style.display = 'none';
                if (overlay2) overlay2.style.display = 'none';
                if (helper) helper.style.display = 'none';

                openVideoModal(() => {
                    showTutorialSpotlight(() => showConnectSpotlight(() => {
                        try { intro.exit(true); } catch (_) { }
                        document.getElementById('tour-connect-spotlight')?.remove();
                    }));
                });

            } else if (action === 'video-no') {
                const tooltip = document.querySelector('.introjs-tooltip') as HTMLElement | null;
                const overlay2 = document.querySelector('.introjs-overlay') as HTMLElement | null;
                const helper = document.querySelector('.introjs-helperLayer') as HTMLElement | null;
                if (tooltip) tooltip.style.display = 'none';
                if (overlay2) overlay2.style.display = 'none';
                if (helper) helper.style.display = 'none';
                showTutorialSpotlight(() => showConnectSpotlight(() => {
                    try { intro.exit(true); } catch (_) { }
                    document.getElementById('tour-connect-spotlight')?.remove();
                }));
            }
        };
        document.addEventListener('click', handleTourClick, true);

        const handleEnd = () => {
            document.removeEventListener('click', handleTourClick, true);
            document.getElementById('tour-video-modal')?.remove();
            document.getElementById('tour-tutorial-spotlight')?.remove();
            document.getElementById('tour-connect-spotlight')?.remove();
            introRef.current = null;
            if (reinitializingRef.current) return;
            // ✅ Marcar como visto permanentemente para que nunca se auto-inicie de nuevo
            localStorage.setItem('tour_permanently_dismissed', 'true');
            if (!isForced) markTourSeen();
            onTourEnd?.();
        };

        intro.onbeforechange(function () {
            return new Promise<boolean>((resolve) => {
                const next: number = (this as any)._currentStep;
                if (next === 1) {
                    const poll = () => {
                        if (window.location.pathname === '/whatsapp') {
                            setTimeout(() => resolve(true), 500);
                        } else {
                            setTimeout(poll, 100);
                        }
                    };
                    poll();
                } else {
                    resolve(true);
                }
            });
        });

        intro.onchange(function () {
            const popups = document.querySelectorAll('.introjs-tooltip');
            if (popups.length > 1) {
                for (let i = 0; i < popups.length - 1; i++) popups[i].remove();
            }
        });

        intro.oncomplete(handleEnd);
        intro.onexit(handleEnd);
        intro.start();
        return true;
    }, [markTourSeen, onTourEnd]);

    // ── Effect 1: auto-start solo para usuarios nuevos (nunca si ya completaron el tour) ──
    useEffect(() => {
        if (hasSeenOnboarding || forceStart) return;
        // ✅ Si el usuario ya descartó el tour permanentemente, nunca auto-iniciar
        if (localStorage.getItem('tour_permanently_dismissed')) return;
        let fallback: ReturnType<typeof setTimeout>;
        const interval = setInterval(() => {
            if (document.getElementById('nav-whatsapp')) {
                clearInterval(interval);
                clearTimeout(fallback);
                startTour(false);
            }
        }, 300);
        fallback = setTimeout(() => { clearInterval(interval); startTour(false); }, 8000);
        return () => { clearInterval(interval); clearTimeout(fallback); cleanExit(); };
    }, [hasSeenOnboarding]); // eslint-disable-line

    // ── Effect 2: toggle manual desde el botón 🎓 del header ─────────────────
    useEffect(() => {
        if (forceStart === undefined) return;
        if (forceStart) {
            startTour(true);
        } else {
            cleanExit();
        }
    }, [forceStart]); // eslint-disable-line

    return null;
};

export default GuidedTour;
